import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { User } from './user';
import { ActivatedRoute } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Client } from '@microsoft/microsoft-graph-client';
import * as MicrosoftGraph from '@microsoft/microsoft-graph-types';
import { environment } from 'src/environments/environment';
import { CommonService } from '../services/common.service';
import { AuthenticationResult } from '@azure/msal-browser';

@Injectable({
  providedIn: 'root'
})

export class AuthService {
  public authenticated: boolean;
  public user: any = undefined;
  public idTokenClaims: any;
  public userRole: any = undefined;
  userSubject = new Subject<any>();
  urlSubject = new Subject<any>();
  agentSearchPageRefresh = new Subject<any>();
  constructor(
    private msalService: MsalService,
    private http: HttpClient,
    private activatedRoute: ActivatedRoute,
    private commonService: CommonService) {
    this.authenticated = false;
    let qryParams = new URLSearchParams(window.location.search);
    if (window.location.href.match(/AppId/i) || sessionStorage.getItem('delegateLogin')) {
      if(qryParams.get('AppId') === environment.portalLinkAppId || sessionStorage.getItem('delegateLogin') === 'true') {
        this.commonService.setIsDelegateLoggedIn(true);
        sessionStorage.removeItem('delegateLogin');
        sessionStorage.removeItem('adminSearchData')
        let url = `${environment.idpFormUrl}/authorize?client_id=${environment.identityServiceClientId}&redirect_uri=${environment.identityServiceRedirectUrl}&response_type=code&scope=openid email&state=Form_${this.genGuid()}`;
        this.activatedRoute.queryParams.subscribe(_params => {
          if (!qryParams.has('code')) {
            window.location.href = url;
          }
        });
      }
      else {
        window.location.href = environment.delegateRedirectUrl;
      }
    } else if (qryParams.has('code')) {
      let code = qryParams.get('code')
      if(code !== null ) {
        sessionStorage.setItem('delegateLogin', 'true');
        sessionStorage.removeItem('adminSearchData');
        this.commonService.setIsDelegateLoggedIn(true);
        let obj = {
          "code": code,
          "client_id": environment.identityServiceClientId,
          "client_secret": environment.identityServiceClientSecret,
          "redirect_uri": environment.identityServiceRedirectUrl
        }
        let url = `${environment.idpFormUrl}/token`;
        this.http.post(url, obj).subscribe((res: any) => {
          this.commonService.setDelegateTokenInfo(res);          
          this.getUserInfo(res);
        })
      } 
    } else {
      this.msalService.handleRedirectObservable().subscribe({
        next: (result: AuthenticationResult) => {
        if (result != null && result.account != null) {
          this.msalService.instance.setActiveAccount(result.account);
          sessionStorage.setItem('accessToken', result.accessToken);
          this.authenticated = true;
          this.getUserProfile();
        } else {
          this.msalService.loginRedirect();
        }
      }
      });
    }
  }

  // Sign out
  signOut(): void {
    this.msalService.logout();
  }

  // Silently request an access token
  async getAccessToken(): Promise<any> {
    // Create request object for Silently request an access token for API calls 
    let requestObject = {
      scopes: environment.scopes,
      appId: environment.appId,
      authority: environment.authority
    };    
    let result = await this.msalService.instance
      .acquireTokenSilent(requestObject)
      .catch((reason) => {
        console.log('Error while getting the silent-token ===>', reason);
      });

    if (result) {
      sessionStorage.setItem('accessTokenForAPICalls', result.accessToken);
      return result.accessToken;
    }
    return null;
  }

  async getAccessTokenForAPICalls(): Promise<any> {
    // Create request object for Silently request an access token for API calls 
    let requestObject = {
      scopes: environment.portalScope,
      appId: environment.appId,
      authority: environment.authority
    };    
    let result = await this.msalService.instance
      .acquireTokenSilent(requestObject)
      .catch((reason) => {
        console.log('Error while getting the silent-token ===>', reason);
      });

    if (result) {
      sessionStorage.setItem('accessTokenForAPICalls', result.accessToken);
      return result.accessToken;
    }
    return null;
  }

  // call this function if you need to get default graph data
  async getUserProfiles(): Promise<any> {
    return this.http.get(environment.graphEndPoint)
      .subscribe(profile => {
        this.updateUser(profile);
      }, (error: any) => {
        console.log('Error while fetching the profile', error);
      });
  }

  getUserAccount() {
    return this.msalService.instance.getActiveAccount();
  }


  getUserRoles(): Promise<User> {
    const userRoles = (this.msalService.instance.getActiveAccount()?.idTokenClaims as any).roles;
    return userRoles;
  }

  async tokenRefresh() {
    return await this.getAccessToken()
  }

  updateUser(e: any) {
    sessionStorage.setItem('accountName', e.onPremisesSamAccountName);
    this.userSubject.next(e);
  }

  async getUserProfile(): Promise<any> {
    if (!this.authenticated) return undefined;

    const graphClient = Client.init({
      // Initialize the Graph client with an auth
      // provider that requests the token from the
      // auth service
      authProvider: async (done) => {
        const token = await this.getAccessToken()
          .catch((reason) => {
            done(reason, null);
          });

        if (token) {
          done(null, token);
        } else {
          done("Could not get an access token", null);
        }
        const tokenDiffScope = await this.getAccessTokenForAPICalls().catch((reason) => {
          done(reason, null);
        });
        if (tokenDiffScope) {
          done(null, tokenDiffScope);
        } else {
          done('Could not get an access token', null);
        }
      }
    });


    //Get the user from Graph (GET /me)
    const graphUser: MicrosoftGraph.User = await graphClient
      .api('/me')
      .select('givenName,surname,onPremisesSamAccountName,displayName,userPrincipalName,onPremisesDistinguishedName,onPremisesDomainName')
      .get();
    this.setAccountName(graphUser);
    this.updateUser(graphUser);
  }


  public subscribeToUserSubject(callback: (user: any) => void): Subscription {
    return this.userSubject.subscribe(callback);
  }

  public subscribeToUrl(callback: (user: any) => void): Subscription {
    return this.urlSubject.subscribe(callback);
  }

  public subscribeagentSearch(callback: (user: any) => void): Subscription {
    return this.agentSearchPageRefresh.subscribe(callback);
  }

  /**set user account name */
  setAccountName(e: any) {
    sessionStorage.setItem('userName', e.displayName);
    sessionStorage.setItem('PremisesDomainName', e.onPremisesDomainName);
    sessionStorage.setItem('userPrincipalName', e.userPrincipalName)
  }

  /**set and entities */
  setEntities(e: any) {
    sessionStorage.setItem('pc-number', e)
  }

  getUserInfo(info: any) {
    let url = `${environment.idpFormUrl}/UserInfo`;
    let obj = { "access_token": info.access_token };
    this.http.post(url, obj).subscribe((res: any) => {
      this.commonService.setDelegateUserInfo(res);
      this.commonService.delegateUserFlow.next(res);
    })
  }

  genGuid() {
    var d = new Date().getTime();//Timestamp
    var d2 = ((typeof performance !== 'undefined') && performance.now && (performance.now() * 1000)) || 0;//Time in microseconds since page-load or 0 if unsupported
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16;//random number between 0 and 16
      if (d > 0) {//Use timestamp until depleted
        r = (d + r) % 16 | 0;
        d = Math.floor(d / 16);
      } else {//Use microseconds since page-load if supported
        r = (d2 + r) % 16 | 0;
        d2 = Math.floor(d2 / 16);
      }
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
  }

}